import React from 'react';
import { Text, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { BlogPost } from '@/src/domain/models/BlogPost';

const SingleVsDoubleContent: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Single elimination and double elimination are the two most widely used tournament
      bracket formats in the world. Choosing between them is one of the first decisions
      every tournament organizer faces — and the right answer depends on your event size,
      available time, and what kind of experience you want to create for your players.
      This guide breaks down both formats so you can make the right call.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How single elimination works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Single elimination is the simplest bracket format: one loss and you're out.
      Players are seeded into a bracket, and each round eliminates exactly half the
      field until one player remains. A single elimination bracket with N players
      requires exactly N−1 matches and Log₂(N) rounds. It's fast, easy to understand,
      and produces a clear winner quickly — which is why it's the default format for
      most casual and large-scale tournaments alike.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      How double elimination works
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Double elimination requires two losses before a player is eliminated. The bracket
      splits into a Winners Bracket (for players with zero losses) and a Losers Bracket
      (for players with one loss). Players in the Winners Bracket can afford one mistake
      and continue fighting. The two brackets converge at a Grand Final, where the
      Winners champion and the Losers champion meet — with a potential reset match if
      the Losers champion wins the first Grand Final game.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Key differences at a glance
    </Text>
    {[
      { label: 'Matches per player', single: 'Minimum 1 match (eliminated first round)', double: 'Minimum 2 matches — guaranteed a second chance' },
      { label: 'Total matches', single: 'N−1 matches', double: '2N−2 or 2N−1 matches (with potential reset)' },
      { label: 'Fairness', single: 'One bad game can eliminate a strong player', double: 'A single off-performance does not end your tournament' },
      { label: 'Complexity', single: 'Simple to run and explain to players', double: 'More complex — two brackets, player routing, reset logic' },
      { label: 'Time required', single: 'Fast — ideal for time-limited events', double: 'Roughly twice as long — needs planning' },
      { label: 'Spectator experience', single: 'Clean bracket, easy to follow progress', double: 'Dramatic — comeback stories from the Losers Bracket' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary, marginBottom: 2 }}>
          {'Single: '}{item.single}
        </Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary }}>
          {'Double: '}{item.double}
        </Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      When to choose single elimination
    </Text>
    {[
      'Large tournaments (64+ players) where running double elimination would take too long',
      'Casual events like office tournaments or friend groups where simplicity matters more than fairness',
      'Time-limited events — a single afternoon, a lunch break tournament, or a quick warm-up',
      'Opening rounds of a larger event (use single elimination for qualifying, then switch to double for the finals)',
      'Any format where the bracket draw itself is part of the excitement and drama',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      When to choose double elimination
    </Text>
    {[
      'Competitive gaming events where skill should determine the outcome, not a single mistake',
      'Fighting game tournaments — double elimination is the industry standard for Smash Bros, Tekken, Street Fighter',
      'Events where you have enough time to run all the additional matches',
      'Smaller tournaments (8–32 players) where the extra matches are manageable',
      'Any event where players have traveled specifically to compete and should get more than one game',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      The hybrid approach
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      Many large tournaments use both formats: a single elimination or pool-based qualifying
      phase to reduce the field quickly, followed by double elimination for the final 8 or 16
      players. This gives you the speed of single elimination for the early rounds while
      still rewarding skill at the top of the bracket.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Both formats in Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly supports both single elimination and double elimination. Single elimination
      is available free for up to 16 participants. Double elimination — with automatic
      Winners/Losers bracket routing and Grand Final reset logic — is available on Tournaly Pro.
      Both formats include a live bracket view, score entry, and shareable tournament links.
    </Text>
  </View>
);

const SingleVsDoubleContentEs: React.FC<{ colors: ThemeColors }> = ({ colors }) => (
  <View>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La eliminación simple y la doble eliminación son los dos formatos de bracket más
      utilizados en el mundo. Elegir entre ellos es una de las primeras decisiones que
      enfrenta todo organizador de torneos — y la respuesta correcta depende del tamaño
      de tu evento, el tiempo disponible y el tipo de experiencia que quieres crear para
      tus jugadores. Esta guía desglosa ambos formatos para que puedas tomar la decisión
      correcta.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona la eliminación simple
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La eliminación simple es el formato de bracket más sencillo: una derrota y quedas
      eliminado. Los jugadores se clasifican en un bracket y cada ronda elimina exactamente
      la mitad del campo hasta que queda un solo jugador. Un bracket de eliminación simple
      con N jugadores requiere exactamente N−1 partidos y Log₂(N) rondas. Es rápido, fácil
      de entender y produce un ganador claro rápidamente — por eso es el formato predeterminado
      para la mayoría de los torneos casuales y a gran escala.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Cómo funciona la doble eliminación
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 20 }}>
      La doble eliminación requiere dos derrotas antes de ser eliminado. El bracket se
      divide en un Bracket de Ganadores (para jugadores con cero derrotas) y un Bracket
      de Perdedores (para jugadores con una derrota). Los jugadores en el Bracket de
      Ganadores pueden permitirse un error y seguir luchando. Los dos brackets convergen
      en una Gran Final donde el campeón de Ganadores y el campeón de Perdedores se
      enfrentan — con un posible partido de reset si el campeón de Perdedores gana el
      primer partido de la Gran Final.
    </Text>

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 8 }}>
      Diferencias clave de un vistazo
    </Text>
    {[
      { label: 'Partidos por jugador', single: 'Mínimo 1 partido (eliminado en primera ronda)', double: 'Mínimo 2 partidos — segunda oportunidad garantizada' },
      { label: 'Total de partidos', single: 'N−1 partidos', double: '2N−2 o 2N−1 partidos (con posible reset)' },
      { label: 'Equidad', single: 'Un mal juego puede eliminar a un jugador fuerte', double: 'Una mala actuación puntual no termina tu torneo' },
      { label: 'Complejidad', single: 'Simple de organizar y explicar a los jugadores', double: 'Más complejo — dos brackets, enrutamiento de jugadores, lógica de reset' },
      { label: 'Tiempo requerido', single: 'Rápido — ideal para eventos con tiempo limitado', double: 'Aproximadamente el doble — requiere planificación' },
      { label: 'Experiencia del espectador', single: 'Bracket limpio, fácil de seguir el progreso', double: 'Dramático — historias de remontada desde el Bracket de Perdedores' },
    ].map((item, index) => (
      <View key={index} style={{ marginBottom: 14 }}>
        <Text style={{ fontSize: 15, fontWeight: '700', color: colors.text, marginBottom: 4 }}>{item.label}</Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary, marginBottom: 2 }}>
          {'Simple: '}{item.single}
        </Text>
        <Text style={{ fontSize: 14, lineHeight: 22, color: colors.textSecondary }}>
          {'Doble: '}{item.double}
        </Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Cuándo elegir eliminación simple
    </Text>
    {[
      'Torneos grandes (64+ jugadores) donde la doble eliminación llevaría demasiado tiempo',
      'Eventos casuales como torneos de oficina o grupos de amigos donde la simplicidad importa más que la equidad',
      'Eventos con tiempo limitado — una tarde, un torneo en la pausa del almuerzo o un calentamiento rápido',
      'Rondas de clasificación de un evento más grande (usa eliminación simple para los clasificatorios, luego doble para las finales)',
      'Cualquier formato donde el sorteo del bracket en sí mismo es parte de la emoción y el drama',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Cuándo elegir doble eliminación
    </Text>
    {[
      'Eventos de gaming competitivo donde la habilidad debe determinar el resultado, no un solo error',
      'Torneos de juegos de lucha — la doble eliminación es el estándar del sector para Smash Bros, Tekken, Street Fighter',
      'Eventos con tiempo suficiente para jugar todos los partidos adicionales',
      'Torneos más pequeños (8–32 jugadores) donde los partidos extra son manejables',
      'Cualquier evento donde los jugadores han viajado específicamente para competir y deben jugar más de un partido',
    ].map((item, index) => (
      <View key={index} style={{ flexDirection: 'row', marginBottom: 10, alignItems: 'flex-start' }}>
        <Text style={{ color: colors.primary, marginRight: 10, fontSize: 16 }}>•</Text>
        <Text style={{ fontSize: 15, lineHeight: 23, color: colors.textSecondary, flex: 1 }}>{item}</Text>
      </View>
    ))}

    <Text style={{ fontSize: 20, fontWeight: '700', color: colors.text, marginBottom: 12, marginTop: 24 }}>
      Ambos formatos en Tournaly
    </Text>
    <Text style={{ fontSize: 16, lineHeight: 26, color: colors.text, marginBottom: 16 }}>
      Tournaly soporta tanto la eliminación simple como la doble eliminación. La eliminación
      simple está disponible gratis para hasta 16 participantes. La doble eliminación — con
      enrutamiento automático entre brackets de Ganadores/Perdedores y lógica de reset de
      Gran Final — está disponible en Tournaly Pro. Ambos formatos incluyen vista de bracket
      en directo, entrada de puntuaciones y enlaces compartibles del torneo.
    </Text>
  </View>
);

export const post: BlogPost = {
  slug: '2026-03-06-single-vs-double-elimination',
  title: 'Single Elimination vs Double Elimination: Which Format to Choose?',
  titleEs: 'Eliminación Simple vs Doble Eliminación: ¿Qué Formato Elegir?',
  date: '2026-03-06',
  summary: 'Single elimination vs double elimination explained — key differences in fairness, match counts, complexity, and time requirements to help you pick the right bracket format for your tournament.',
  summaryEs: 'Eliminación simple vs doble eliminación explicadas — diferencias clave en equidad, número de partidos, complejidad y tiempo para ayudarte a elegir el formato de bracket correcto para tu torneo.',
  tags: ['Guide', 'Formats', 'Single Elimination', 'Double Elimination'],
  Content: SingleVsDoubleContent,
  ContentEs: SingleVsDoubleContentEs,
};
